// stage-prerun — canvas-play-button (Trello 8nnBRRwp). The pre-run overlay over the dark
// runner stage (a 56px circle + "Press Run to start your program" caption) LOOKS clickable
// but today does nothing — `.stage-prerun` sets `pointer-events: none`, it is purely
// decorative. This module wraps the glyph + caption in a real `<button>` (markup change is
// in the two templates, not here) and wires it to start the program the same way the
// toolbar `#runner-run` button does.
//
// Wired ONCE from main.js, uniformly across every surface that ships the overlay — unlike
// the per-surface `*-chrome.js` modules this is NOT dispatched by data-surface.
//
//   wire(core) — core is the editor-core instance main.js has already constructed+loaded.
//
// MUST be null-safe: `pgzero-clientside-editor.html` loads this same Vite entry with no
// `#stage-prerun` markup at all. wire() runs OUTSIDE editor-core's per-listener emit()
// try/catch (inside main.js's load().then() body), so an unguarded throw here would reject
// that promise before `surface.wire()` runs and kill the whole editor on a surface this
// change does not target.
export function wire(core) {
  var prerun = document.getElementById('stage-prerun');
  var btn = document.getElementById('stage-prerun-btn');
  var runBtn = document.getElementById('runner-run');
  if (!prerun || !btn || !runBtn) { return; }

  var frame = document.getElementById('runner-frame');
  var captionEl = btn.querySelector('.stage-prerun-text');
  // Capture the server-rendered default wording ONCE, before the first possible swap,
  // rather than hard-coding it — the two templates own that copy, not this module.
  var defaultCaption = captionEl ? captionEl.textContent : '';
  var CLICKABLE_CAPTION = 'Click to run your program';

  // Availability is derived from the toolbar Run button itself — no surface allow/deny
  // list. This automatically keeps the control inert on predict/trace-capture (which
  // hide #runner-run), while the runner is still loading, and under runBlocked (Pygame
  // without JSPI) — and stays correct for any future gated surface by default.
  function available() {
    return !runBtn.disabled && runBtn.getClientRects().length > 0;
  }

  // ONE idempotent setter that touches exactly three things together: the button's
  // disabled state, the container's aria-hidden, and the caption. No state machine, no
  // "revert" special case — the fail-closed guarantee holds in every branch. The ring
  // colour is deliberately NOT a fourth thing here: it is CSS-derived from :disabled.
  function applyState(isAvailable) {
    btn.disabled = !isAvailable;
    if (isAvailable) {
      prerun.removeAttribute('aria-hidden');
    } else {
      prerun.setAttribute('aria-hidden', 'true');
    }
    if (captionEl) {
      captionEl.textContent = isAvailable ? CLICKABLE_CAPTION : defaultCaption;
    }
  }

  // Re-evaluated on 'ready' and on any repeat 'ready' (effectively unreachable in
  // product beyond window.__editorCore.restart(), kept as a one-guard defensiveness).
  // Never re-shows an already-hidden overlay: once prerun.hidden is true, `.stage-
  // prerun[hidden] { display: none; }` wins regardless of aria-hidden.
  core.on('ready', function () { applyState(available()); });

  // Click listener bound exactly once (wire() itself only runs once from main.js).
  btn.addEventListener('click', function () {
    // Re-check at click time: closes the (effectively unreachable) window where Run
    // became unavailable after this control was enabled. This is the SOLE branch that
    // disables a button the user just activated and still has focus on, so the blur
    // MUST precede the disable or focus drops to <body> with aria-hidden restored onto
    // its ancestor (the axe aria-hidden-focus violation).
    if (!available()) {
      btn.blur();
      applyState(false);
      return;
    }
    // core.run() MUST be the first effectful statement here, called synchronously —
    // nothing async (no promise/timeout/rAF) may precede it, or the pgzero audio
    // unlock silently breaks because it depends on the still-live user gesture.
    core.run();
    // Focus the runner iframe, not #runner-run: focus staying on the toolbar Run would
    // make Space/Enter re-run the program instead of reaching the canvas. Guarded on
    // existence; structurally cannot fire on a run this button did not start, because
    // it lives here rather than being deferred to the state listener below.
    if (frame) { frame.focus(); }
  });

  // The overlay hides on the runner's OWN state — never eagerly after our click, and
  // never by wrapping core.run(): most run call sites are core.request* helpers that
  // post runner:run from inside editor-core, not core.run() itself. This also clears
  // the overlay for a run started from ANY origin (toolbar, this button, or a
  // *-chrome.js module's own request), and is never re-shown afterwards.
  core.on('state', function (state) {
    if (prerun.hidden) { return; }
    if (state === 'booting' || state === 'running') {
      prerun.hidden = true;
    }
  });
}
